import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { toast } from 'react-toastify';

// @mui material components
import { Grid, Card } from '@mui/material';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

// Material Dashboard 2 React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

// Material Dashboard 2 React example components
import DataTable from 'examples/Tables/DataTable';
import ActionsPopover from 'components/ActionsPopover/ActionsPopover';
import AddAmenity from './components/AddAmenity';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import ImageComponent from '../../components/ImageComponent/ImageComponent';

import amenityPlaceholder from '../../assets/images/no-amenity.png';

// Data
import {
  getAmenities,
  addAmenity,
  updateAmenity,
  deleteAmenity,
  resetAmenitiesList,
} from 'store/reducers/amenitySlice';
import { amenitiesColumns } from 'constants/columns.constants';

const Amenities = () => {
  const [isLoading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const { amenitiesList } = useSelector((state) => state.amenitySlice);

  const [selectedAmenity, setSelectedAmenity] = useState(null);
  const [isEditAmenity, setEditAmenity] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [isDeleteModal, setDeleteModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [sortBy, setSortBy] = useState({
    id: 'id',
    desc: true,
  });
  const [searchTerm, setSearchTerm] = useState('');

  const columns = useMemo(() => {
    return amenitiesColumns;
  }, []);

  const rows =
    amenitiesList?.amenitiesData?.length > 0
      ? amenitiesList?.amenitiesData?.map((amenity) => {
          return {
            id: amenity?.amenityId,
            name: amenity?.name ?? 'NA',
            image: (
              <ImageComponent
                imgStyles={{
                  height: '32px',
                  width: '32px',
                  objectFit: 'contain',
                  margin: '0',
                  verticalAlign: 'bottom',
                  borderRadius: '0',
                }}
                imageUrl={amenity?.imageUrl ?? ''}
                spinnerSize={30}
                fallbackImg={amenityPlaceholder}
              />
            ),
            actions: (
              <ActionsPopover
                editTitle={'Edit Amenity'}
                deleteTitle={'Delete Amenity'}
                onEditClick={() => {
                  setSelectedAmenity(amenity);
                  setEditAmenity(true);
                  setOpenModal(true);
                }}
                onDeleteClick={() => {
                  setSelectedAmenity(amenity);
                  setDeleteModal(true);
                }}
              />
            ),
          };
        })
      : [];

  async function fetchAmenities() {
    try {
      setLoading(true);
      await dispatch(
        getAmenities({
          page: currentPage + 1,
          limit: perPage,
          sortField: sortBy?.id ?? 'id',
          sortType: sortBy?.desc && sortBy?.desc ? 'DESC' : 'ASC',
          search: searchTerm,
        })
      );
    } catch (error) {
      console.log({ error });
    } finally {
      setLoading(false);
    }
  }

  const onCloseModal = () => {
    setSelectedAmenity(null);
    setEditAmenity(false);
    setOpenModal(false);
    setDeleteModal(false);
  };

  const onCreateAmenity = async (amenityName, amenityImage) => {
    try {
      await dispatch(
        addAmenity({
          name: amenityName,
          image: amenityImage,
        })
      );
      onCloseModal();
      if (currentPage === 0) {
        fetchAmenities();
      } else {
        setCurrentPage(0);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message ?? 'Something went wrong!');
    }
  };

  const onUpdateAmenity = async (amenityId, amenityName, amenityImage) => {
    try {
      await dispatch(
        updateAmenity({
          id: amenityId,
          body: {
            name: amenityName,
            image: amenityImage,
          },
        })
      );
      onCloseModal();
      if (currentPage === 0) {
        fetchAmenities();
      } else {
        setCurrentPage(0);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message ?? 'Something went wrong!');
    }
  };

  const onDeleteAmenity = async () => {
    try {
      await dispatch(deleteAmenity(selectedAmenity?.amenityId));
      if (currentPage === 0) {
        fetchAmenities();
      } else {
        setCurrentPage(0);
      }
    } catch (error) {
      console.log({ error });
    } finally {
      onCloseModal();
    }
  };

  const onFetchDataDebounced = debounce(fetchAmenities, 0);

  useEffect(() => {
    onFetchDataDebounced();

    return () => {
      onFetchDataDebounced.cancel();
    };
  }, [currentPage, sortBy, searchTerm, perPage]);

  useEffect(() => {
    return () => {
      dispatch(resetAmenitiesList());
    };
  }, []);

  return (
    <MDBox pt={6} pb={3}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Amenities
              </MDTypography>
              <MDButton
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setOpenModal(true)}
              >
                Add Amenity
              </MDButton>
            </MDBox>
            <MDBox
              sx={{
                position: 'relative',
                paddingTop: '85px',
                paddingBottom: {
                  xs: '150px',
                  sm: '95px',
                },
              }}
            >
              <DataTable
                table={{ columns, rows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: perPage }}
                showTotalEntries
                canSearch
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                perPage={perPage}
                setPerPage={setPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPage={amenitiesList?.totalPage}
                totalRecords={amenitiesList?.totalCount}
                hiddenColumns={['id']}
                queryPageSortBy={[sortBy]}
                setSortBy={setSortBy}
                isLoading={isLoading}
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>
      {openModal && (
        <AddAmenity
          openModal={openModal}
          onCloseModal={onCloseModal}
          onCreateAmenity={onCreateAmenity}
          onUpdateAmenity={onUpdateAmenity}
          isEdit={isEditAmenity}
          selectedAmenity={selectedAmenity}
        />
      )}
      {isDeleteModal && (
        <ConfirmModal
          open={isDeleteModal}
          onCloseModal={onCloseModal}
          modalText="Are you sure you want to delete this amenity?"
          okayText="Yes, delete"
          cancelText="No, cancel"
          onOkayClick={onDeleteAmenity}
        />
      )}
    </MDBox>
  );
};

export default Amenities;
